import React, { useState } from "react";
import { toast } from "sonner";
import { publicLink } from "@/lib/landerDomain";
import { Copy, Trophy } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { MODES, RotatorForm } from "./RotatorForm";

const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40";
const pct = (n, d = 0) => `${(Number(n || 0) * 100).toFixed(d)}%`;

function progressText(r) {
  if (r.mode === "gradual") return r.leader ? "Sending more visits to the leader as results come in" : "Still gathering results — traffic is close to even";
  if (r.testing_done) return "Testing done — most traffic goes to the winner";
  const done = r.variants.filter((v) => v.visits >= r.min_visits).length;
  return `Testing: even split until each page has ${r.min_visits} visits (${done}/${r.variants.length} pages done)`;
}

const VariantRow = ({ v, rid }) => (
  <tr className="border-t border-zinc-200" data-testid={`rotator-variant-${rid}-${v.slug}`}>
    <td className="px-3 py-1.5">
      <span className="font-bold">{v.name}</span>
      {v.is_leader && <span className="ml-1.5 inline-flex items-center gap-0.5 border border-emerald-700 bg-emerald-100 px-1 text-[9px] uppercase tracking-widest text-emerald-800" data-testid={`rotator-leader-${rid}-${v.slug}`}><Trophy size={9} weight="fill" /> Winner</span>}
    </td>
    <td className="px-3 py-1.5 text-right" data-testid={`rotator-visits-${rid}-${v.slug}`}>{v.visits}</td>
    <td className="px-3 py-1.5 text-right" data-testid={`rotator-leads-${rid}-${v.slug}`}>{v.leads}</td>
    <td className="px-3 py-1.5 text-right font-bold" data-testid={`rotator-conv-${rid}-${v.slug}`}>{pct(v.conversion, 1)}</td>
    <td className="px-3 py-1.5 w-32">
      <div className="h-2 bg-zinc-200"><div className="h-2 bg-black transition-[width] duration-500" style={{ width: pct(v.share) }} /></div>
      <div className="text-[9px] text-zinc-500 mt-0.5">{pct(v.share)} of visits</div>
    </td>
  </tr>
);

export function RotatorCard({ r, pages, onChanged }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = publicLink(`/go/${r.code}`);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setMode(null); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed — select the link and copy it"));

  if (mode === "edit") return <RotatorForm pages={pages} initial={r} onSaved={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />;
  const paused = r.status === "paused";
  return (
    <div className="border-2 border-black bg-white" data-testid={`rotator-card-${r.id}`}>
      <div className="px-3 py-2 border-b border-black flex flex-wrap items-center gap-2">
        <span className="font-chivo font-black uppercase tracking-tight">{r.name}</span>
        <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${paused ? "border-zinc-500 bg-zinc-100" : "border-emerald-700 bg-emerald-100 text-emerald-800"}`} data-testid={`rotator-status-${r.id}`}>{paused ? "Paused" : "Live"}</span>
        <span className="text-[10.5px] text-zinc-600">{MODES.find((m) => m.key === r.mode)?.label}</span>
      </div>
      <div className="px-3 py-2 flex flex-wrap items-center gap-2 bg-zinc-50 border-b border-zinc-200">
        <code className="text-xs break-all flex-1 min-w-[200px]" data-testid={`rotator-url-${r.id}`}>{url}</code>
        <button type="button" onClick={copy} className={`${BTN} inline-flex items-center gap-1`} data-testid={`rotator-copy-${r.id}`}><Copy size={11} weight="bold" /> Copy</button>
      </div>
      <div className="px-3 py-1.5 text-[11px] text-zinc-700" data-testid={`rotator-progress-${r.id}`}>{progressText(r)} · {r.total_visits} visits · {r.total_leads} form submissions</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-zinc-100 text-[10px] uppercase tracking-widest"><th className="text-left px-3 py-1.5">Page</th><th className="text-right px-3 py-1.5">Visits</th><th className="text-right px-3 py-1.5">Forms sent</th><th className="text-right px-3 py-1.5">Per visit</th><th className="text-left px-3 py-1.5">Traffic</th></tr></thead>
          <tbody>{r.variants.map((v) => <VariantRow key={v.slug} v={v} rid={r.id} />)}</tbody>
        </table>
      </div>
      <div className="px-3 py-2 flex flex-wrap gap-1.5 border-t border-zinc-200">
        <button type="button" disabled={busy} onClick={() => act(() => api.patch(`/vendor/rotators/${r.id}`, { status: paused ? "active" : "paused" }), paused ? "Link is live again" : "Link paused")} className={BTN} data-testid={`rotator-toggle-${r.id}`}>{paused ? "Resume" : "Pause"}</button>
        <button type="button" onClick={() => setMode("edit")} className={BTN} data-testid={`rotator-edit-${r.id}`}>Edit pages &amp; rules</button>
        {mode === "reset"
          ? <button type="button" disabled={busy} onClick={() => act(() => api.post(`/vendor/rotators/${r.id}/reset`), "Stats reset — testing starts over")} className={`${BTN} !bg-amber-500`} data-testid={`rotator-reset-confirm-${r.id}`}>Confirm reset</button>
          : <button type="button" onClick={() => setMode("reset")} className={BTN} data-testid={`rotator-reset-${r.id}`}>Start test over</button>}
        {mode === "delete"
          ? <button type="button" disabled={busy} onClick={() => act(() => api.delete(`/vendor/rotators/${r.id}`), "Link deleted")} className={`${BTN} !bg-red-600 !text-white`} data-testid={`rotator-delete-confirm-${r.id}`}>Confirm delete</button>
          : <button type="button" onClick={() => setMode("delete")} className={BTN} data-testid={`rotator-delete-${r.id}`}>Delete</button>}
      </div>
    </div>
  );
}
